import React, { useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Box, Stack } from "@mui/material";
import { Kanban, List, Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "../../../components/Button";
import { CustomSelector } from "../../../components/CustomSelector";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import { getData } from "../../../components/hooks/getData";
import { DataTableToolbar, toolbarViewModeButtonSx } from "../../../components/DataTable";
import { ViewModeSelector } from "../../../components/ViewModeSelector";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";
import { useAuth } from "../../../components/contexts/AuthContext";
import {
  CLIENTIFY_STAGES_ENDPOINT,
  listClientifyOwners,
} from "../../../services/clientifyService";
import { CloseDealModal } from "./CloseDealModal";
import { ContactFormPanel } from "./ContactFormPanel";
import { DealFormPanel } from "./DealFormPanel";
import { columnKeyForDeal } from "./clientifyDeals.js";
import { VistaKanban } from "./VistaKanban";
import { Vistatabla } from "./Vistatabla";

const FILTER_OPTIONS = [
  { value: "overdue", label: "Vencidas" },
  { value: "open", label: "Abiertas" },
  { value: "won", label: "Ganadas" },
  { value: "lost", label: "Perdidas" },
  { value: "all", label: "Todas" },
];

const VIEW_MODE_OPTIONS = [
  { value: "list", label: "Contactos", icon: List },
  { value: "board", label: "Funnel", icon: Kanban },
];

const SORT_DIRECTION_OPTIONS = [
  { value: "asc", label: "Ascendiente" },
  { value: "desc", label: "Descendiente" },
];

const SORT_OPTIONS_CONTACTS = [
  { value: "last_seen", label: "Últimos vistos" },
  { value: "-created", label: "Últimos añadidos" },
  { value: "last_contact", label: "Últimos contactados" },
];

const SORT_FIELD_OPTIONS_DEALS = [
  { value: "created", label: "Hora de creación" },
  { value: "actual_closed_date", label: "Hora de cierre" },
  { value: "modified", label: "Hora de modificación" },
  { value: "last_seen", label: "Hora de la última actividad" },
];

export function Clientify() {
  const { colors, theme } = useCustomTheme();
  const { user, interceptorReady } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const {
    setPageContext,
    selectedFilter: selectedGlobalFilter,
    selectedFilterData,
    changeSelectedFilter,
    filterRefresh,
  } = useOutletContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [viewMode, setViewMode] = useState("list");
  // "Todas" by default: with "Vencidas" a deal just created (open) is nowhere on the board.
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortField, setSortField] = useState("created");
  const [contactsOrderBy, setContactsOrderBy] = useState("last_seen");
  const searchDebounceTimerRef = useRef(null);

  const [dealPanel, setDealPanel] = useState({ open: false, dealId: null, stageId: null });
  const [contactPanel, setContactPanel] = useState({ open: false, contactId: null });
  const [closeRequest, setCloseRequest] = useState({ deal: null, result: null });
  // Deals moved or edited on the board since the columns last fetched.
  const [dealOverrides, setDealOverrides] = useState({});
  const [contactsRefreshKey, setContactsRefreshKey] = useState(0);

  const { data: stagesData, isLoading: stagesLoading, error: stagesError } = getData(
    CLIENTIFY_STAGES_ENDPOINT,
    interceptorReady,
    0
  );
  const stages = React.useMemo(() => {
    const rows = stagesData?.results || stagesData;
    return Array.isArray(rows) ? [...rows].sort((a, b) => a.position - b.position) : [];
  }, [stagesData]);

  // A superadmin has no Clientify user of their own: they look at one
  // connected user at a time (Clientify cannot filter contacts by several),
  // picked among the agency selected in the global filter.
  const selectedAgencyId =
    isSuperadmin && selectedGlobalFilter && selectedFilterData?.id !== "global"
      ? selectedFilterData?.id
      : null;
  const { data: ownerRows = [], isLoading: ownersLoading } = useQuery({
    queryKey: ["clientify-owner-filter", selectedAgencyId],
    queryFn: () => listClientifyOwners(selectedAgencyId),
    enabled: interceptorReady && isSuperadmin,
    staleTime: 5 * 60 * 1000,
  });
  const [ownerFilter, setOwnerFilter] = useState(null);
  const firstOwnerId = ownerRows[0]?.user ?? null;
  React.useEffect(() => {
    setOwnerFilter(selectedAgencyId ? firstOwnerId : null);
  }, [selectedAgencyId, firstOwnerId]);
  const ownerFilterOptions = React.useMemo(
    () => [
      ...(selectedAgencyId ? [] : [{ value: null, label: "Todos los propietarios" }]),
      ...ownerRows.map((row) => ({
        value: row.user,
        label: row.user_name || row.user_email,
      })),
    ],
    [ownerRows, selectedAgencyId]
  );
  const ownerClientifyId = isSuperadmin
    ? ownerRows.find((row) => row.user === ownerFilter)?.clientify_user_id
    : undefined;
  const agencyWithoutConnections =
    Boolean(selectedAgencyId) && !ownersLoading && ownerRows.length === 0;

  // Ajustar ordenamiento cuando cambia la vista
  React.useEffect(() => {
    // Más recientes primero en ambas vistas: lo recién creado queda arriba.
    setSortDirection("desc");
  }, [viewMode]);

  React.useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed.length >= 2 || trimmed === "") {
        setSearchTerm(trimmed);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  React.useEffect(() => {
    setDealOverrides({});
  }, [searchTerm, selectedStatusFilter, sortDirection, sortField, ownerClientifyId, viewMode]);

  // `index` is the slot a card was dropped on in its new column.
  const handleDealChanged = React.useCallback((deal, index) => {
    setDealOverrides((prev) => ({
      ...prev,
      [deal.id]: { deal, columnKey: columnKeyForDeal(deal), index },
    }));
  }, []);

  // Parked on a column no board has, so every column drops it and its amount.
  const handleDealDeleted = React.useCallback((deal) => {
    setDealOverrides((prev) => ({ ...prev, [deal.id]: { deal, columnKey: "deleted" } }));
  }, []);

  const handleRequestClose = React.useCallback((deal, result) => {
    setCloseRequest({ deal, result });
  }, []);

  const handleDealClosed = React.useCallback(
    (saved, previous) => {
      handleDealChanged({ ...previous, ...saved });
      setCloseRequest({ deal: null, result: null });
      setDealPanel({ open: false, dealId: null, stageId: null });
    },
    [handleDealChanged]
  );

  const openDeal = React.useCallback((deal) => {
    setDealPanel({ open: true, dealId: deal.id, stageId: null });
  }, []);

  const createDeal = React.useCallback((stageId) => {
    setDealPanel({ open: true, dealId: null, stageId: stageId ?? null });
  }, []);

  const handleClearGlobalFilter = React.useCallback(() => {
    changeSelectedFilter("global");
  }, [changeSelectedFilter]);

  const globalFilterChip = React.useMemo(() => {
    if (
      user?.role !== "superadmin" ||
      !selectedGlobalFilter ||
      !selectedFilterData?.name
    ) {
      return null;
    }
    return {
      key: "global-filter",
      label: selectedFilterData.name,
      onRemove: handleClearGlobalFilter,
    };
  }, [
    user?.role,
    selectedGlobalFilter,
    selectedFilterData,
    handleClearGlobalFilter,
  ]);

  React.useEffect(() => {
    const breadcrumbItems =
      user?.role === "superadmin"
        ? selectedGlobalFilter && selectedFilterData
          ? [
            {
              label: "Vista Global",
              onClick: () => changeSelectedFilter("global"),
            },
            { label: `Agencia: ${selectedFilterData.name}` },
          ]
          : [{ label: "Vista Global" }]
        : undefined;

    const isContactsView = viewMode === "list";

    setPageContext({
      title: (
        <>
          CLIENTIFY:
          <span style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}>
            {isContactsView ? "Contactos" : "Funnel nuevo cliente"}
          </span>
        </>
      ),
      subtitle: isContactsView
        ? "Gestión de contactos importados desde Clientify"
        : "Gestión de oportunidades de venta importadas desde Clientify",
      showTitle: true,
      breadcrumbItems,
      rightContent: (
        <>
          <a
            href={isContactsView ? "https://new.clientify.com/contacts" : "https://new.clientify.com/sales/deals/board"}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: colors.link || theme.palette.primary.main,
              textDecoration: "none",
              cursor: "pointer",
              fontSize: "0.9375rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
            onMouseLeave={(e) => e.target.style.textDecoration = "none"}
          >
            Enlace a clientify: {isContactsView ? "contactos" : "funnel nuevo cliente"}
          </a>
          <Button
            size="sm"
            onClick={() =>
              isContactsView
                ? setContactPanel({ open: true, contactId: null })
                : createDeal(null)
            }
            disabled={!isContactsView && stages.length === 0}
            sx={{ gap: 0.5, whiteSpace: "nowrap" }}
          >
            <Plus size={16} />
            {isContactsView ? "Agregar contacto" : "Agregar oportunidad"}
          </Button>
        </>
      ),
    });
  }, [
    setPageContext,
    user?.role,
    selectedGlobalFilter,
    selectedFilterData,
    changeSelectedFilter,
    theme.palette.text.secondary,
    theme.palette.primary.main,
    colors.link,
    viewMode,
    stages.length,
    createDeal,
  ]);

  const sortOrderBy = React.useMemo(() => {
    if (viewMode === "list") {
      return contactsOrderBy;
    }
    return sortDirection === "desc" ? `-${sortField}` : sortField;
  }, [viewMode, contactsOrderBy, sortDirection, sortField]);

  const isContactsView = viewMode === "list";
  const searchPlaceholder = isContactsView ? "Buscar contactos..." : "Buscar oportunidades...";
  const sortFieldOptions = isContactsView ? SORT_OPTIONS_CONTACTS : SORT_FIELD_OPTIONS_DEALS;

  const toolbarProps = {
    searchValue: searchInput,
    onSearchChange: setSearchInput,
    searchPlaceholder,
    // Los tabs de estado solo aplican a las oportunidades (vista tablero).
    ...(viewMode === "board" && {
      tabs: FILTER_OPTIONS,
      activeTab: selectedStatusFilter,
      onTabChange: setSelectedStatusFilter,
    }),
    filterChips: globalFilterChip ? [globalFilterChip] : [],
    sortFieldOptions,
    sortField: isContactsView ? contactsOrderBy : sortField,
    sortDirection,
    sortDirectionOptions: SORT_DIRECTION_OPTIONS,
    onSortFieldChange: isContactsView ? setContactsOrderBy : setSortField,
    onSortDirectionChange: setSortDirection,
    showSortDirection: !isContactsView,
    toolbarEnd: (
      <>
        <ViewModeSelector
          options={VIEW_MODE_OPTIONS}
          value={viewMode}
          onChange={setViewMode}
          tooltipTitle="Escoger vista"
          buttonSx={toolbarViewModeButtonSx}
        />
        {isSuperadmin && (
          <Box sx={{ width: { xs: "100%", sm: 240 }, flexShrink: 0 }}>
            <CustomSelector
              searchable
              placeholder={ownersLoading ? "Cargando propietarios..." : "Propietario"}
              value={ownerFilter}
              onChange={setOwnerFilter}
              options={ownerFilterOptions}
              disabled={ownersLoading || agencyWithoutConnections}
            />
          </Box>
        )}
      </>
    ),
  };

  return (
    <Stack spacing={viewMode === "list" ? 1.5 : 2}>
      {viewMode === "list" ? (
        agencyWithoutConnections ? (
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <DataTableToolbar {...toolbarProps} />
            <Box sx={{ p: 6 }}>
              <EmptyStateMessage message="Nadie de esta agencia está conectado a Clientify todavía." />
            </Box>
          </Box>
        ) : (
          <Vistatabla
            searchTerm={searchTerm}
            ownerId={ownerClientifyId}
            orderBy={sortOrderBy}
            refreshKey={contactsRefreshKey + (isSuperadmin ? filterRefresh : 0)}
            onOpenContact={(contactId) => setContactPanel({ open: true, contactId })}
            toolbarProps={toolbarProps}
          />
        )
      ) : (
        <>
          <DataTableToolbar variant="standalone" {...toolbarProps} />

          {agencyWithoutConnections ? (
            <Box sx={{ py: 8 }}>
              <EmptyStateMessage message="Nadie de esta agencia está conectado a Clientify todavía." />
            </Box>
          ) : (
            <VistaKanban
              stages={stages}
              stagesLoading={stagesLoading}
              stagesError={stagesError}
              searchTerm={searchTerm}
              selectedFilter={selectedStatusFilter}
              ownerId={ownerClientifyId}
              orderBy={sortOrderBy}
              refreshKey={isSuperadmin ? filterRefresh : 0}
              overrides={dealOverrides}
              onDealMoved={handleDealChanged}
              onOpenDeal={openDeal}
              onCreateDeal={createDeal}
              onRequestClose={handleRequestClose}
            />
          )}
        </>
      )}

      <DealFormPanel
        open={dealPanel.open}
        dealId={dealPanel.dealId}
        initialStageId={dealPanel.stageId}
        stages={stages}
        ownerUserId={ownerFilter ?? undefined}
        ownerClientifyId={ownerClientifyId}
        onClose={() => setDealPanel({ open: false, dealId: null, stageId: null })}
        onSaved={(saved, previous) => {
          handleDealChanged({ ...(previous || {}), ...saved });
          // A new deal is open: make sure the status filter does not hide it.
          if (!previous && !["all", "open"].includes(selectedStatusFilter)) {
            setSelectedStatusFilter("all");
          }
        }}
        onDeleted={handleDealDeleted}
        onRequestClose={handleRequestClose}
      />

      <ContactFormPanel
        open={contactPanel.open}
        contactId={contactPanel.contactId}
        ownerUserId={ownerFilter ?? undefined}
        onClose={() => setContactPanel({ open: false, contactId: null })}
        onSaved={() => setContactsRefreshKey((key) => key + 1)}
      />

      <CloseDealModal
        deal={closeRequest.deal}
        result={closeRequest.result}
        onCancel={() => setCloseRequest({ deal: null, result: null })}
        onClosed={handleDealClosed}
      />
    </Stack>
  );
}
